/**
 * Map State Management Overview
 *
 * This application uses three different approaches to manage map state:
 *
 * 1. React Context (MapDisplayContext)
 *    - Original implementation for managing map state
 *    - Used for local component state that doesn't need to be shared across the app
 *    - Good for state that changes frequently and doesn't need persistence
 *    - Example: UI state like showFilterPane, menuExpanded
 *
 * 2. URL Parameters (useUrlQueryDisplayState)
 *    - Used for state that should be linkable and shareable
 *    - Enables deep linking and browser history navigation
 *    - Good for state that represents the current view/selection
 *    - Example: activePin, displayMode
 *
 * 3. Singleton Service (MapDisplayContextService)
 *    - Mirrors the React context state to provide access outside the component tree
 *    - Enables initialization of map state when switching to map from other pages
 *    - This is not a clean pattern but is necessary for our navigation flow
 *    - Example: onGetDirections callback, newItinerary
 *
 * Note: The singleton service pattern is used as a pragmatic solution to enable
 * cross-page state initialization. While not ideal architecturally, it solves
 * the immediate need to access map state from outside the component tree.
 */

import * as React from 'react';
import OLMap from 'ol/Map';

import { DigitalMapPin } from 'models/digitalMap';
import { ProductSummary } from 'models/product';
import { GuidancePageCoupon } from 'models/guidancePage';
import { CheckoutFormValues } from './Checkout/formValues';
import { useRouter } from 'next/router';
import { useMapUrl } from 'hooks/useMapUrl';
import { Itinerary } from 'models/itinerary';
import { UrlQueryDisplayState, useUrlQueryDisplayState } from './useUrlQueryDisplayState';
import { ItemSelection } from './RestaurantDetails/RestaurantDetails';
import MapDisplayContextService from './MapDisplayContextService';

export type DisplayMode =
  | 'BASE'
  | 'SEARCH_RESULTS'
  | 'SEARCH_INPUT'
  | 'DESKTOP_SEARCH_RESULTS'
  | 'SHOW_DIRECTIONS_ROUTE';
export type SortType = 'RELEVANCE' | 'DISTANCE';
export type PinInfoDisplayStatus = 'HIDDEN' | 'SUMMARY' | 'DETAILS';
export type RestaurantPage = 'MAIN' | 'ITEM' | 'CHECKOUT' | 'STATUS';

export interface MapDisplayContextType {
  displayMode: DisplayMode;
  setDisplayMode: (mode: DisplayMode) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  sortBy: SortType;
  setSortBy: (sortBy: SortType) => void;
  showFilterPane: boolean;
  setShowFilterPane: (show: boolean) => void;
  searchResultsListPosition: 'MINIMIZED' | 'MIDWAY' | 'MAXIMIZED';
  setSearchResultsListPosition: (position: 'MINIMIZED' | 'MIDWAY' | 'MAXIMIZED') => void;
  productInCart: ProductSummary | null;
  setProductInCart: (product: ProductSummary | null) => void;
  savedCartParams: CheckoutFormValues | null;
  setSavedCartParams: (params: CheckoutFormValues | null) => void;
  activeStampPinIdx: number | null;
  setActiveStampPinIdx: (idx: number | null) => void;
  showStampRallyModal: boolean;
  setShowStampRallyModal: (show: boolean) => void;
  activeCouponViewContext: {
    coupon: GuidancePageCoupon;
    pin: DigitalMapPin;
  } | null;
  setActiveCouponViewContext: (
    coupon: {
      coupon: GuidancePageCoupon;
      pin: DigitalMapPin;
    } | null
  ) => void;
  menuExpanded: boolean;
  setMenuExpanded: (expanded: boolean) => void;
  googleMap: google.maps.Map | null;
  openLayersMap: OLMap | null;
  setOpenLayersMap: (map: OLMap | null) => void;
  setActiveRestaurantId: (restaurantId: string | null) => void;
  activeRestaurantId: string | null;
  setActiveOpenTableRestaurantId: (restaurantId: string | null) => void;
  activeOpenTableRestaurantId: string | null;
  orderStatusRestaurantId: string | null;
  setOrderStatusRestaurantId: (restaurantId: string | null) => void;
  orderStatusRestaurantOrderId: string | null;
  setOrderStatusRestaurantOrderId: (restaurantOrderId: string | null) => void;
  setShouldRefreshCustomerReservations: (shouldRefresh: boolean) => void;
  shouldRefreshCustomerReservations: boolean;
  onGetDirections: (origin: google.maps.LatLng, destinationPin: DigitalMapPin) => void;
  selectedFloor: number;
  setSelectedFloor: (floor: number) => void;
  setShouldRefreshCustomerRestaurantOrders: (shouldRefresh: boolean) => void;
  activeIframeUrl: string | null;
  setActiveIframeUrl: (url: string | null) => void;
  activeItineraryPinKeys: string[];
  setActiveItineraryPinKeys: (keys: string[]) => void;
  newItinerary: Itinerary | null;
  setNewItinerary: (itinerary: Itinerary | null) => void;
  selectedItineraryDate: string | null;
  setSelectedItineraryDate: (date: string | null) => void;
  showStarredPinList: boolean;
  setShowStarredPinList: (show: boolean) => void;
  activeItemSelection: ItemSelection | null;
  setActiveItemSelection: (selection: ItemSelection | null) => void;
  activeRestaurantPageStack: RestaurantPage[];
  setActiveRestaurantPageStack: React.Dispatch<React.SetStateAction<RestaurantPage[]>>;
}

interface MapDisplayContextTypeWithHelpers extends MapDisplayContextType {
  resetDisplay(): void;
  clearCart(): void;
  restoreSavedDisplayState(): void;
  saveDisplayState(): void;
  clearSavedDisplayState(): void;
}

const defaultContext: MapDisplayContextType = {
  displayMode: 'BASE',
  setDisplayMode: () => {},
  searchText: '',
  setSearchText: () => {},
  sortBy: 'RELEVANCE',
  setSortBy: () => {},
  showFilterPane: false,
  setShowFilterPane: () => {},
  searchResultsListPosition: 'MINIMIZED',
  setSearchResultsListPosition: () => {},
  productInCart: null,
  setProductInCart: () => {},
  savedCartParams: null,
  setSavedCartParams: () => {},
  activeStampPinIdx: null,
  setActiveStampPinIdx: () => {},
  showStampRallyModal: false,
  setShowStampRallyModal: () => {},
  activeCouponViewContext: null,
  setActiveCouponViewContext: () => {},
  menuExpanded: false,
  setMenuExpanded: () => {},
  googleMap: null,
  openLayersMap: null,
  setOpenLayersMap: () => {},
  setActiveRestaurantId: () => {},
  activeRestaurantId: null,
  setActiveOpenTableRestaurantId: () => {},
  activeOpenTableRestaurantId: null,
  orderStatusRestaurantId: null,
  setOrderStatusRestaurantId: () => {},
  orderStatusRestaurantOrderId: null,
  setOrderStatusRestaurantOrderId: () => {},
  setShouldRefreshCustomerReservations: () => {},
  shouldRefreshCustomerReservations: false,
  onGetDirections: () => {},
  selectedFloor: 1,
  setSelectedFloor: () => {},
  setShouldRefreshCustomerRestaurantOrders: () => {},
  activeIframeUrl: null,
  setActiveIframeUrl: () => {},
  activeItineraryPinKeys: [],
  setActiveItineraryPinKeys: () => {},
  newItinerary: null,
  setNewItinerary: () => {},
  selectedItineraryDate: null,
  setSelectedItineraryDate: () => {},
  showStarredPinList: false,
  setShowStarredPinList: () => {},
  activeItemSelection: null,
  setActiveItemSelection: () => {},
  activeRestaurantPageStack: ['MAIN'],
  setActiveRestaurantPageStack: () => {},
};
export const MapDisplayContext = React.createContext<MapDisplayContextType>(defaultContext);

interface MapDisplayContextProviderProps {
  children: React.ReactNode;
  value: MapDisplayContextType;
}

export const MapDisplayContextProvider: React.FC<MapDisplayContextProviderProps> = ({
  children,
  value,
}) => {
  React.useEffect(() => {
    MapDisplayContextService.setMapDisplayContext(value);
  }, [value]);

  return <MapDisplayContext.Provider value={value}>{children}</MapDisplayContext.Provider>;
};

export const useMapDisplayContext = (): MapDisplayContextTypeWithHelpers & UrlQueryDisplayState => {
  const context = React.useContext(MapDisplayContext);
  const router = useRouter();
  const pathName = router.asPath;
  const mapUrl = useMapUrl();
  const urlQueryDisplayState = useUrlQueryDisplayState();

  return {
    ...context,
    ...urlQueryDisplayState,
    resetDisplay: () => {
      context.setDisplayMode('BASE');
      context.setSearchText('');
      context.setShowFilterPane(false);
      context.setProductInCart(null);
      context.setSavedCartParams(null);
      context.setActiveRestaurantId(null);
      context.setActiveOpenTableRestaurantId(null);
      context.setOrderStatusRestaurantId(null);
      context.setOrderStatusRestaurantOrderId(null);
      context.setSelectedFloor(1);
      context.setActiveIframeUrl(null);
      router.push(mapUrl, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    clearCart: () => {
      context.setProductInCart(null);
      context.setSavedCartParams(null);
    },
    saveDisplayState: () => {
      sessionStorage.setItem(
        'map-ctx',
        JSON.stringify({
          displayMode: context.displayMode,
          searchText: context.searchText,
          sortBy: context.sortBy,
          showFilterPane: context.showFilterPane,
          searchResultsListPosition: context.searchResultsListPosition,
          productInCart: context.productInCart,
          savedCartParams: context.savedCartParams,
          activeStampPinIdx: context.activeStampPinIdx,
          showStampRallyModal: context.showStampRallyModal,
          activeCouponViewContext: context.activeCouponViewContext,
          menuExpanded: context.menuExpanded,
          activeRestaurantId: context.activeRestaurantId,
          orderStatusRestaurantId: context.orderStatusRestaurantId,
          orderStatusRestaurantOrderId: context.orderStatusRestaurantOrderId,
          pathName,
          selectedFloor: context.selectedFloor,
          activeIframeUrl: context.activeIframeUrl,
          showStarredPinList: context.showStarredPinList,
        })
      );
    },
    restoreSavedDisplayState: () => {
      const ctx = sessionStorage.getItem('map-ctx');
      if (!ctx) {
        return null;
      }

      const savedCtx: MapDisplayContextType & { pathName: string } = JSON.parse(ctx);

      context.setProductInCart(savedCtx.productInCart);
      context.setSavedCartParams(savedCtx.savedCartParams);
      context.setDisplayMode(savedCtx.displayMode);
      context.setSearchText(savedCtx.searchText);
      context.setSortBy(savedCtx.sortBy);
      context.setShowFilterPane(savedCtx.showFilterPane);
      context.setSearchResultsListPosition(savedCtx.searchResultsListPosition);
      context.setActiveStampPinIdx(savedCtx.activeStampPinIdx);
      context.setShowStampRallyModal(savedCtx.showStampRallyModal);
      context.setActiveCouponViewContext(savedCtx.activeCouponViewContext);
      context.setMenuExpanded(savedCtx.menuExpanded);
      context.setActiveRestaurantId(savedCtx.activeRestaurantId);
      context.setActiveOpenTableRestaurantId(savedCtx.activeOpenTableRestaurantId);
      context.setOrderStatusRestaurantId(savedCtx.orderStatusRestaurantId);
      context.setOrderStatusRestaurantOrderId(savedCtx.orderStatusRestaurantOrderId);
      context.setSelectedFloor(savedCtx.selectedFloor);
      context.setActiveIframeUrl(savedCtx.activeIframeUrl);
      context.setShowStarredPinList(savedCtx.showStarredPinList);
      sessionStorage.removeItem('map-ctx');

      if (savedCtx.pathName) {
        router.push(savedCtx.pathName, undefined, {
          shallow: true,
          scroll: false,
        });
      }
    },
    clearSavedDisplayState: () => {
      sessionStorage.removeItem('map-ctx');
    },
  };
};
